import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAnnouncementBannerState from './AnnouncementBannerStateHook';
import IconButton from '../common/icon-button/IconButton';
import Cookie from 'js-cookie';
import classNames from 'classnames';
import { COOKIE_ANOUNCEMENT_BANNER_SHOWN, COOKIE_ANOUNCEMENT_BANNER_CLOSE_DURATION } from '@mdc/constants';
import { dayjs } from '@mdc/services';

import './AnnouncementBanner.scss';

/**
 * When using the banner component the parent should have
 * a `withAnnouncementBanner` class when the component is visible.
 * Example:
 * ```
 *  const [isAnnouncementBannerVisible] = useAnnouncementBannerState();
 *  const layoutClassnames = classnames({
 *      withAnnouncementBanner: isAnnouncementBannerVisible,
 *  });
 *  <Container className={layoutClassnames}>
 *      <AnnouncementBanner {...} />
 *  </Container>
 * }}
 * ```
 *
 * @param {*} { content: 'Jsx component' }
 */
const COOKIE_ANOUNCEMENT_BANNER_LOCAL = 'announcement-banner';
const AnnouncementBanner = ({ content, className, shouldExpireOn }) => {
    const [isVisible, setIsVisible] = useAnnouncementBannerState();
    const [isClient, setIsClient] = useState(false);
    const isCurrentEnvLocal = process.env.ENVIRONMENT === 'local';
    const cookieName = isCurrentEnvLocal ? COOKIE_ANOUNCEMENT_BANNER_LOCAL : COOKIE_ANOUNCEMENT_BANNER_SHOWN;
    const expirationDate = shouldExpireOn ? new Date(shouldExpireOn) : COOKIE_ANOUNCEMENT_BANNER_CLOSE_DURATION;
    const cookieOptions = {
        expires: expirationDate,
        secure: isCurrentEnvLocal ? false : true,
        sameSite: 'strict'
    };

    useEffect(() => {
        setIsClient(true);
        const cookieState = Cookie.get(cookieName);
        const shouldBeDisplayed = shouldExpireOn ? dayjs(shouldExpireOn).isAfter() : true;

        if (!cookieState && shouldBeDisplayed) {
            setIsVisible(true);
        }
    }, []);

    const announcementBannerClassName = classNames('announcementBanner', className);

    if (!isClient || !content || !isVisible) {
        return null;
    }

    const onClose = () => {
        Cookie.set(cookieName, 'closed', cookieOptions);
        setIsVisible(false);
    };

    return (
        <div className={announcementBannerClassName}>
            <div className="announcementBannerContent">
                {content}
            </div>
            <IconButton iconClass="icon-close" onClick={onClose}/>
        </div>
    );
};

export default AnnouncementBanner;

AnnouncementBanner.propTypes = {
    content: PropTypes.any.isRequired,
    className: PropTypes.string,
    shouldExpireOn: PropTypes.any
};
